import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Save, Zap, MessageSquareWarning, Link2, AtSign, Trash2, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead, PremiumBanner } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";
import { CsvField } from "@/components/CsvField";

const ACTIONS = [
  { value: "delete", label: "Delete only" },
  { value: "warn", label: "Delete + Warn" },
  { value: "timeout", label: "Delete + Timeout" },
  { value: "kick", label: "Delete + Kick" },
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const inputCls = "w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm";

function RuleCard({ icon: Icon, title, desc, testid, rule, onChange, children }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-volt" />
          <div>
            <div className="text-sm font-semibold">{title}</div>
            <div className="text-xs text-zinc-500">{desc}</div>
          </div>
        </div>
        <Toggle testid={`${testid}-enabled`} checked={!!rule.enabled} onChange={(v) => onChange({ ...rule, enabled: v })} />
      </div>
      {rule.enabled && (
        <div className="space-y-3">
          {children}
          <Field label="Punishment">
            <select data-testid={`${testid}-action`} value={rule.action} onChange={(e) => onChange({ ...rule, action: e.target.value })} className={inputCls}>
              {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
        </div>
      )}
    </div>
  );
}

export default function AutoMod() {
  const { guildId, guild, refreshKey } = useGuild();
  const locked = false;
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  const [warnings, setWarnings] = useState([]);

  const loadWarnings = () => api.get(`/guilds/${guildId}/warnings`).then((r) => setWarnings(r.data)).catch(() => {});
  useEffect(() => {
    api.get(`/guilds/${guildId}/modules/automod`).then((r) => setCfg(r.data));
    loadWarnings();
  }, [guildId, refreshKey]);

  const saveRaw = async () => {
    try {
      await api.put(`/guilds/${guildId}/modules/automod`, { enabled: cfg.enabled, config: cfg.config });
      toast.success("Auto-Moderation settings saved");
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };

  const removeWarning = async (id) => {
    try {
      await api.delete(`/guilds/${guildId}/warnings/${id}`);
      toast.success("Warning cleared");
      loadWarnings();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const clearAll = async () => {
    try {
      await api.delete(`/guilds/${guildId}/warnings`);
      toast.success("All warnings cleared");
      loadWarnings();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };

  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const c = cfg.config;
  const set = (k, v) => setCfg({ ...cfg, config: { ...c, [k]: v } });
  const setRule = (k, v) => set(k, v);

  return (
    <div data-testid="automod-page">
      <PageHead title="Auto-Moderation" desc="Automatically detect and punish spam, profanity, links and mass-mentions.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Enable Auto-Mod</span>
            <Toggle testid="automod-enabled" checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} /></div>
          <button data-testid="automod-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>

      <PremiumBanner guildId={guildId} tier="Pro" locked={locked} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <RuleCard icon={Zap} title="Spam / Flood" desc="Too many messages in a short window" testid="rule-spam" rule={c.spam} onChange={(v) => setRule("spam", v)}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Max messages"><input data-testid="spam-max" type="number" min="2" value={c.spam.max_messages} onChange={(e) => setRule("spam", { ...c.spam, max_messages: +e.target.value })} className={inputCls} /></Field>
              <Field label="Within (seconds)"><input data-testid="spam-interval" type="number" min="1" value={c.spam.interval_seconds} onChange={(e) => setRule("spam", { ...c.spam, interval_seconds: +e.target.value })} className={inputCls} /></Field>
            </div>
          </RuleCard>

          <RuleCard icon={MessageSquareWarning} title="Profanity Filter" desc="Remove messages containing banned words" testid="rule-profanity" rule={c.profanity} onChange={(v) => setRule("profanity", v)}>
            <Field label="Banned words (comma separated)">
              <CsvField as="textarea" data-testid="profanity-words" rows={3} value={c.profanity.words} onChange={(v) => setRule("profanity", { ...c.profanity, words: v })} className={inputCls} />
            </Field>
          </RuleCard>

          <RuleCard icon={Link2} title="Links & Invites" desc="Block Discord invites and external links" testid="rule-links" rule={c.links} onChange={(v) => setRule("links", v)}>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">Block Discord invites</span>
              <Toggle testid="links-invites" checked={!!c.links.block_invites} onChange={(v) => setRule("links", { ...c.links, block_invites: v })} /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">Block all external links</span>
              <Toggle testid="links-external" checked={!!c.links.block_links} onChange={(v) => setRule("links", { ...c.links, block_links: v })} /></div>
            {c.links.block_links && (
              <Field label="Allowed domains (whitelist)">
                <CsvField data-testid="links-whitelist" value={c.links.whitelist} onChange={(v) => setRule("links", { ...c.links, whitelist: v })} className={inputCls} placeholder="youtube.com, github.com" />
              </Field>
            )}
          </RuleCard>

          <RuleCard icon={AtSign} title="Mass Mentions" desc="Block @everyone/@here and mention spam" testid="rule-mentions" rule={c.mentions} onChange={(v) => setRule("mentions", v)}>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">Block @everyone / @here</span>
              <Toggle testid="mentions-everyone" checked={!!c.mentions.block_everyone} onChange={(v) => setRule("mentions", { ...c.mentions, block_everyone: v })} /></div>
            <Field label="Max user mentions per message"><input data-testid="mentions-max" type="number" min="1" value={c.mentions.max_mentions} onChange={(e) => setRule("mentions", { ...c.mentions, max_mentions: +e.target.value })} className={inputCls} /></Field>
          </RuleCard>

          <div className="md:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-volt" /><span className="text-sm font-semibold">Global Settings</span></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Exempt roles (comma separated)"><CsvField data-testid="ignored-roles" value={c.ignored_roles} onChange={(v) => set("ignored_roles", v)} className={inputCls} placeholder="Admin, Moderator, Staff" /></Field>
              <Field label="Ignored channels (comma separated)"><CsvField data-testid="ignored-channels" value={c.ignored_channels} onChange={(v) => set("ignored_channels", v)} className={inputCls} placeholder="bot-commands" /></Field>
              <Field label="Timeout duration (minutes)"><input data-testid="timeout-minutes" type="number" min="1" value={c.timeout_minutes} onChange={(e) => set("timeout_minutes", +e.target.value)} className={inputCls} /></Field>
              <Field label="Warn threshold → auto-timeout"><input data-testid="warn-threshold" type="number" min="1" value={c.warn_threshold} onChange={(e) => set("warn_threshold", +e.target.value)} className={inputCls} /></Field>
              <Field label="Mod-log channel"><ChannelSelect guildId={guildId} value={c.mod_log_channel} onChange={(v) => set("mod_log_channel", v)} testid="modlog-channel" placeholder="— select a channel —" /></Field>
              <div className="flex items-center justify-between pt-6"><span className="text-sm text-zinc-300">DM users when actioned</span>
                <Toggle testid="dm-user" checked={!!c.dm_user} onChange={(v) => set("dm_user", v)} /></div>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-400" /> Warnings History</h3>
            {warnings.length > 0 && <button data-testid="clear-warnings" onClick={clearAll} className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Clear all</button>}
          </div>
          <div className="space-y-2 max-h-[560px] overflow-y-auto">
            {warnings.length === 0 && <p className="text-zinc-600 text-sm">No auto-mod warnings yet.</p>}
            {warnings.map((w) => (
              <div key={w.id} data-testid="warning-row" className="flex items-center gap-3 border-l-2 border-red-500/40 pl-3 py-2 bg-black/30 rounded-r-md">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{w.username}</div>
                  <div className="text-xs text-zinc-500 truncate">{w.rule} · {w.reason}</div>
                </div>
                <button data-testid="remove-warning" onClick={() => removeWarning(w.id)} className="text-zinc-600 hover:text-red-400 shrink-0"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
